import React from "react";
import { Box, Button, Container, Grid, Stack, Typography } from "@mui/material";
import "../../styles/Home.css";
import aboutus_main from "../../Assets/aboutus_main.png";
import aboutus_sec1 from "../../Assets/aboutus_sec1.png";
import aboutus_sec2 from "../../Assets/aboutus_sec2.png";
import about_us from "../../Assets/about_us.png";
import { try_for_free } from "../../url";
import Map from "./map";

// Shared styles for better consistency
const styles = {
  button: {
    backgroundColor: "#25B2E8",
    fontFamily: "Roboto",
    height: "50px",
    fontWeight: "bold",
    borderRadius: "12px",
    fontSize: { xs: "0.9rem", md: "1.1rem" },
    color: "white",
    textTransform: "capitalize",
    px: { xs: 3, md: 4 },
    py: 1.5,
    "&:hover": {
      backgroundColor: "#1EA8D9",
      transform: "translateY(-2px)",
      boxShadow: "0px 8px 25px rgba(37, 178, 232, 0.3)",
    },
    transition: "all 0.3s ease",
  },
  sectionText: {
    fontFamily: "Roboto",
    lineHeight: { xs: 1.6, md: 1.7 },
    fontWeight: "normal",
    fontSize: { xs: "1rem", md: "1.1rem" },
    color: "#6E6B7B",
    mb: 2,
  },
  container: {
    maxWidth: { xs: "100%", sm: "90%", md: "85%", lg: "80%", xl: "75%" },
  },
};

const AboutUs = () => (
  <>
    {/* Hero Section */}
    <Box
      sx={{
        mt: { xs: 8, md: 10 },
        pt: { xs: 4, sm: 6, md: 8 },
        pb: { xs: 4, md: 6 },
        backgroundImage: `linear-gradient(rgba(22, 98, 167, 0.1), rgba(37, 178, 232, 0.05)), url(${about_us})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: { xs: "10vh", md: "10vh" },
      }}
    >
      <Container maxWidth="lg" sx={styles.container}>
        <Box textAlign="center">
          <Typography
            variant="h1"
            sx={{
              fontFamily: "Roboto",
              fontWeight: "bold",
              fontSize: { xs: "3rem", md: "5rem" },
              color: "#1662A7",
              textShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              mb: 2,
            }}
          >
            About Us
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#6E6B7B",
              fontSize: { xs: "1.1rem", md: "1.3rem" },
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            Discover how RequireSign is revolutionizing <br></br>digital
            signatures worldwide
          </Typography>
        </Box>
      </Container>
    </Box>

    {/* Main Content Section */}
    <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: "#F8F8F8" }}>
      <Container maxWidth="lg" sx={styles.container}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: "bold",
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  color: "#1662A7",
                  lineHeight: 1.2,
                }}
              >
                RequireSign
              </Typography>

              <Typography
                variant="h4"
                sx={{
                  color: "#6E6B7B",
                  fontSize: { xs: "1.3rem", md: "1.6rem" },
                  fontWeight: "medium",
                  mb: 2,
                }}
              >
                Simplifying{" "}
                <Box
                  component="span"
                  sx={{
                    color: "#25B2E8",
                    fontWeight: "bold",
                  }}
                >
                  Digital Signatures
                </Box>
              </Typography>

              <Typography variant="body1" sx={styles.sectionText}>
                For decades, e-signatures have transformed the way businesses
                operate globally. As the market has grown, so too has the need
                for a more accessible and affordable solution.
              </Typography>

              <Typography variant="body1" sx={styles.sectionText}>
                That's where RequireSign comes in.
              </Typography>

              <Box sx={{ pt: 2 }}>
                <Button
                  component="a"
                  href={try_for_free}
                  target="_blank"
                  rel="noreferrer"
                  sx={styles.button}
                >
                  Try for FREE
                </Button>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: { xs: 4, md: 0 },
              }}
            >
              <Box
                component="img"
                src={aboutus_main}
                alt="RequireSign Platform"
                sx={{
                  width: "100%",
                  maxWidth: "1500px",
                  borderRadius: "15px",
                  boxShadow: "0px 8px 40px rgba(0, 0, 0, 0.15)",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    {/* Feature Sections */}
    <Box sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg" sx={styles.container}>
        <Grid container spacing={6}>
          {/* Feature 1 */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-start" },
                mb: { xs: 3, md: 0 },
              }}
            >
              <Box
                component="img"
                src={aboutus_sec1}
                alt="Expert Team"
                sx={{
                  width: { xs: "90%", md: "95%" },
                  borderRadius: "15px",
                  boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.1)",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                px: { xs: 2, md: 3 },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  ...styles.sectionText,
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  lineHeight: 1.8,
                }}
              >
                Our team of seasoned tech experts has a proven track record of
                delivering innovative solutions for leading corporations
                worldwide. By leveraging our deep understanding of both business
                and technology, we've developed an e-signature platform that's
                not only powerful but also easy to use.
              </Typography>
            </Box>
          </Grid>

          {/* Feature 2 */}
          <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                px: { xs: 2, md: 3 },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  ...styles.sectionText,
                  fontSize: { xs: "1.1rem", md: "1.2rem" },
                  lineHeight: 1.8,
                }}
              >
                Whether you're a small local business or a large global
                enterprise, RequireSign offers a cost-effective solution that
                empowers you to streamline your processes, reduce paperwork, and
                enhance security. Join us in embracing the future of digital
                signatures.
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                mb: { xs: 3, md: 0 },
              }}
            >
              <Box
                component="img"
                src={aboutus_sec2}
                alt="Global Solution"
                sx={{
                  width: { xs: "90%", md: "95%" },
                  borderRadius: "15px",
                  boxShadow: "0px 8px 30px rgba(0, 0, 0, 0.1)",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                  },
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    <Map />
  </>
);

export default AboutUs;
